import { ApolloCache, MutationHookOptions } from '@apollo/client';
import {
  deriveDocumentBasedOnType,
  findCachedQueryByType,
  getReplacementsFromCachedQuery,
  updateCachedQueryByType,
} from '@replacements/utils/query';
import {
  UpdatePhoneticReplacementMutation,
  useUpdatePhoneticReplacementMutation,
} from '@replacements/graphql/operation.generated';
import logger from '@replacements/utils/phoneticLogger';
import {
  PhoneticLibraryType,
  PhoneticReplacement,
  PhoneticReplacementInputs,
} from '../../../../../server/graphql/__generated.types';

type ReplacementCache = ApolloCache<UpdatePhoneticReplacementMutation>;

const updatePhoneticReplacementCache = (
  activeType: PhoneticLibraryType,
  cache: ReplacementCache,
  phoneticReplacement: UpdatePhoneticReplacementMutation['phoneticReplacementUpdate']['replacement']
) => {
  logger.prefix = `[updatePhoneticReplacementCache]`;
  logger.info(`updating: ${activeType} cache`);
  const document = deriveDocumentBasedOnType(activeType);
  const cachedQuery = findCachedQueryByType(cache, document, activeType);
  const replacements = getReplacementsFromCachedQuery(
    activeType,
    document,
    cachedQuery
  );
  const updatedReplacements = replacements.map(
    (replacement: PhoneticReplacement) => {
      if (replacement.id === phoneticReplacement?.id) {
        return phoneticReplacement;
      }
      return replacement;
    }
  );
  const updatedQueryBody = updateCachedQueryByType(
    activeType,
    cachedQuery,
    updatedReplacements
  );
  if (updatedQueryBody) {
    cache.writeQuery({ query: document, data: updatedQueryBody });
    logger.info(`succesfully wrote query body`);
    return;
  }
  logger.error(`updated query body not found: ${activeType}`);
  logger.prefix = '';
};

const useUpdatePhoneticReplacement = ({
  onCompleted,
}: MutationHookOptions<UpdatePhoneticReplacementMutation>): [
  (options?: {
    variables: {
      inputs: PhoneticReplacementInputs;
    };
  }) => void,
  { loading: boolean }
] => {
  const config: Parameters<typeof useUpdatePhoneticReplacementMutation>[0] = {
    update: (cache, { data }) => {
      if (data?.phoneticReplacementUpdate.replacement) {
        updatePhoneticReplacementCache(
          data.phoneticReplacementUpdate.replacement.libraryId
            ? PhoneticLibraryType.Team
            : PhoneticLibraryType.Personal,
          cache,
          data.phoneticReplacementUpdate.replacement
        );
      }
    },
    onCompleted: data => {
      if (onCompleted) {
        onCompleted(data);
      }
    },
  };

  const [updatePhoneticReplacement, { loading }] =
    useUpdatePhoneticReplacementMutation(config);

  return [updatePhoneticReplacement, { loading }];
};

export default useUpdatePhoneticReplacement;
